import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import debounce from 'lodash.debounce'
import { Delete, Edit } from '@mui/icons-material'
import {
  Link as MUILink,
  Container,
  Button,
  Stack,
  Skeleton,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { useGetOrgCredentialTypes } from '@/service-library/hooks/org-credential-types'
import { useGetOrganization } from '@/service-library/hooks/organizations'
import { useGetProjectTemplates } from '@/service-library/hooks/project-templates'
import {
  useDeleteProjectWithDefaults,
  useUpdateProject,
} from '@/service-library/hooks/projects'
import queryKeys from '@/service-library/query-keys'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { ProjectCategory } from '@/types/projects'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import LabeledData from '@/components/labeled-data/LabeledData'
import LargeHeading from '@/components/large-heading/LargeHeading'
import ListAutocomplete from '@/components/list-autocomplete/ListAutocomplete'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import ProjectCategoryAutocomplete from '@/components/projects/ProjectCategoryAutocomplete'
import DeleteProjectDialog from './DeleteProjectDialog'
import EditProjectDescriptionDialog from './EditProjectDescriptionDialog'
import EditProjectNameDialog from './EditProjectNameDialog'

export default function ProjectSettings() {
  const { project, queryKey } = useProjectContext()
  const isSuperUser = useIsSuperUser()

  const { organization, isPending: isLoadingOrg } = useGetOrganization({
    id: project.org_id,
  })

  const { orgCredentialTypes } = useGetOrgCredentialTypes({
    enabled: isSuperUser,
  })

  const { projectTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1',
      project_id: project.id,
    },
  })

  const editOverlay = useOverlay()
  const editDescriptionOverlay = useOverlay()
  const deleteOverlay = useOverlay()

  const navigate = useNavigate()

  const { updateProject } = useUpdateProject({
    detailQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Unable to update project. Please try again later.')
    },
  })

  const { deleteProject } = useDeleteProjectWithDefaults({
    sideEffectQueryKeys: [queryKeys.projects.lists()],
    onSuccess: () => {
      showSuccessSnackbar('Project Deleted')
      navigate('/projects')
    },
    onError: () => {
      showErrorSnackbar('Unable to delete project. Please try again later.')
    },
  })

  const onDeleteProject = () => {
    deleteOverlay.close()
    deleteProject(project.id)
  }

  const debouncedUpdateProject = useMemo(
    () => debounce(updateProject, 250),
    [updateProject],
  )

  const [internalCategory, setInternalCategory] = useState(
    project.project_category as ProjectCategory,
  )

  function handleRemoveDupEntities(checked: boolean) {
    debouncedUpdateProject({ ...project, remove_duplicate_entities: checked })
  }

  function handleExcludeFromDocRouting(checked: boolean) {
    debouncedUpdateProject({ ...project, exclude_from_doc_routing: checked })
  }

  function handleCategoryChange(category: ProjectCategory) {
    setInternalCategory(category)
    updateProject(
      {
        ...project,
        project_category_id: category.id,
      },
      {
        onError: () =>
          setInternalCategory(project.project_category as ProjectCategory),
      },
    )
  }

  return (
    <Container maxWidth="md" sx={{ height: '100%' }}>
      <ProjectBreadcrumb label="Project General" url="../general" />

      <Stack justifyContent="space-between" sx={{ height: '100%' }}>
        <Stack spacing={4}>
          <LargeHeading
            heading={project.name}
            subHeading="Project Settings"
            actions={
              <>
                <Button
                  variant="text"
                  startIcon={<Edit />}
                  onClick={editOverlay.open}
                >
                  Edit
                </Button>
                <Tooltip
                  title={
                    projectTemplates.length > 0
                      ? 'Cannot delete template projects'
                      : ''
                  }
                >
                  <span>
                    <Button
                      variant="text"
                      startIcon={<Delete />}
                      color="error"
                      onClick={() => deleteOverlay.open()}
                      disabled={projectTemplates.length > 0}
                    >
                      Delete
                    </Button>
                  </span>
                </Tooltip>
                <CopyIDButton stringToCopy={project.id} />
                <DeleteProjectDialog
                  name={project.name}
                  onDelete={onDeleteProject}
                  overlay={deleteOverlay}
                />
                <EditProjectNameDialog
                  overlay={editOverlay}
                  project={project}
                  updateProject={updateProject}
                />
                <EditProjectDescriptionDialog
                  overlay={editDescriptionOverlay}
                  project={project}
                  updateProject={updateProject}
                />
              </>
            }
          />

          <ProjectCategoryAutocomplete
            selected={internalCategory}
            onCategoryChange={handleCategoryChange}
            sx={{ maxWidth: 350 }}
          />
          <LabeledData
            label="Description"
            data={
              <div>
                {project.description || (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontStyle="italic"
                  >
                    No description
                  </Typography>
                )}
                <div>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<Edit />}
                    onClick={editDescriptionOverlay.open}
                    sx={{ mt: 1 }}
                  >
                    Edit Description
                  </Button>
                </div>
              </div>
            }
          />
          {/* Wrapping div to keep this from expanding to full width of page */}
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={project.exclude_from_doc_routing}
                  sx={{ mr: 1 }}
                />
              }
              label={
                <>
                  <Typography>Exclude from AI Auto Assign on Upload</Typography>
                  <Typography variant="caption">
                    If enabled, the AI Auto Assign feature will exclude this
                    project from being automatically assigned documents upon
                    upload.
                  </Typography>
                </>
              }
              onChange={(_e, checked) => handleExcludeFromDocRouting(checked)}
            />
          </div>
          {/* Wrapping div to keep this from expanding to full width of page */}
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={project.remove_duplicate_entities}
                  sx={{ mr: 1 }}
                />
              }
              label={
                <>
                  <Typography>Remove Subsequent Duplicate Entities</Typography>
                  <Typography variant="caption">
                    If enabled, any additional predicted values that are likely
                    duplicate entities or unexpected values will be
                    automatically removed. Enable this if seeing chips predicted
                    from multiple locations in the document.
                  </Typography>
                </>
              }
              onChange={(_e, checked) => handleRemoveDupEntities(checked)}
            />
          </div>
          {/* Wrapping div to keep this from expanding to full width of page */}
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={project.documents_contain_pii}
                  sx={{ mr: 1 }}
                />
              }
              label={
                <>
                  <Typography>
                    Documents Contain Personally Identifiable Information (PII)
                  </Typography>
                  <Typography variant="caption">
                    Documents uploaded to this project may contain information
                    that can be used to identify an individual.
                  </Typography>
                </>
              }
              onChange={(_e, checked) =>
                debouncedUpdateProject({
                  ...project,
                  documents_contain_pii: checked,
                })
              }
            />
          </div>
          {project.org_id && (
            <LabeledData
              label="Parent Organization"
              data={
                <>
                  {isLoadingOrg ? (
                    <Skeleton width={125} />
                  ) : (
                    <MUILink
                      component={Link}
                      underline="hover"
                      to={`/settings/organizations/${organization?.id}`}
                    >
                      {organization?.name}
                    </MUILink>
                  )}
                </>
              }
            />
          )}
          {isSuperUser && (
            <ListAutocomplete
              options={orgCredentialTypes}
              selected={
                orgCredentialTypes.find(
                  (t) => t.id === project.org_credential_type_id,
                ) ?? null
              }
              setSelected={(value) =>
                updateProject({
                  ...project,
                  org_credential_type_id: value.id,
                })
              }
              onChange={(_e, value) =>
                updateProject({
                  ...project,
                  org_credential_type_id: value?.id ?? null,
                })
              }
              disableClearable={false}
              label="Org Credential Type"
              helperText="The type of credential this project expects for the data it retrieves. Superuser only."
              sx={{ maxWidth: 350 }}
            />
          )}
        </Stack>
      </Stack>
    </Container>
  )
}
